/**
 * Auxiliary django-allauth headless endpoints used by Aegix One that
 * are NOT part of the `LoginAdapter` contract in `@aegix/auth-identity`.
 *
 * The session-lifecycle flows (login / OAuth / passkey / signup /
 * logout / probe) live in the lib's `createAllauthAdapter`. These
 * endpoints stay in app code because they cover side-flows the
 * adapter intentionally does not model:
 *
 *   - `getProviderRedirectURL` — initiates the OAuth redirect flow;
 *     the callback step (`completeOAuthCallback`) goes through
 *     `loginAdapter.loginWithOAuth({provider, params})` per the
 *     ADR-118 contract.
 *   - `verifyEmail` — completes signup's email-verification step.
 *   - `requestPasswordReset` / `resetPassword` — password recovery.
 *   - `getPasskeyCreationOptions` / `registerPasskey` — passkey
 *     REGISTRATION for already-authenticated users (distinct from
 *     passkey LOGIN, which is `loginAdapter.loginWithPasskey`).
 *
 * Backed by the allauth headless app endpoint at
 * `/_allauth/app/v1/auth/*` — same surface the adapter targets,
 * so the cookie/Set-Cookie semantics stay consistent across the
 * lifecycle and side flows.
 *
 * These side flows ride `deviceIdFetch`, the same stamped
 * transport the login adapter uses (`lib/deviceIdHeader.ts`) —
 * `verifyEmail` is a credential MINT (its response may carry
 * `meta.access_token`), and `getProviderRedirectURL` initiates the
 * OAuth flow whose completion mints, so both must carry the
 * `x-device-id` the backend binds the session to. The
 * remaining flows carry it for uniformity; a browser with nothing
 * persisted sends no header at all.
 */

import {
  getSharedSessionToken,
  setSharedSessionToken,
  clearSharedSessionToken,
} from '@aegix/auth-identity';
import { deviceIdFetch } from './deviceIdHeader';

function getAllauthURL(path: string): string {
  const apiURL = window.__ENV?.NX_API_URL || '';
  const isAppClientEnabled = window.__ENV?.NX_ALLAUTH_APP_CLIENT_ENABLED === 'true';
  const pathPrefix = isAppClientEnabled ? '/_allauth/app/v1' : '/_allauth/browser/v1';
  return `${apiURL}${pathPrefix}${path}`;
}

// ─── Response shapes (only fields the helpers actually read) ──────────

export interface AllAuthEndpointResponse {
  data?: {
    user?: {
      id: number;
      email: string;
      first_name?: string;
      last_name?: string;
    };
    url?: string;
  };
  meta?: {
    access_token?: string;
    session_token?: string;
  };
  errors?: Array<{ message?: string }>;
  detail?: string;
}

async function allAuthRequest(
  path: string,
  options: RequestInit = {},
): Promise<AllAuthEndpointResponse> {
  const url = getAllauthURL(path);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  const isAppClientEnabled = window.__ENV?.NX_ALLAUTH_APP_CLIENT_ENABLED === 'true';

  if (isAppClientEnabled) {
    const sessionToken = getSharedSessionToken();
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }
  }

  const response = await deviceIdFetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  let data: AllAuthEndpointResponse | null = null;
  try {
    data = (await response.json()) as AllAuthEndpointResponse;
  } catch {
    data = null;
  }

  if (isAppClientEnabled) {
    if (data?.meta?.session_token) {
      setSharedSessionToken(data.meta.session_token);
    } else if (data?.meta?.access_token) {
      clearSharedSessionToken();
    }
  }

  if (!response.ok) {
    // 401 + flows on the body is allauth's "needs another step" signal —
    // pass it through so the caller can decide.
    if (response.status === 401 && data?.data) {
      return data;
    }
    const message =
      data?.errors?.[0]?.message ||
      data?.detail ||
      `Request failed: ${response.status}`;
    throw new Error(message);
  }

  return data ?? {};
}

// ─── OAuth (redirect-initiation only) ───────────────────────────────

/**
 * Request the provider redirect URL for the OAuth login flow. The
 * caller navigates the browser there; the provider redirects back to
 * `callbackURL` with query params that the AuthCallback page feeds
 * into `loginAdapter.loginWithOAuth({provider, params})`.
 */
export async function getProviderRedirectURL(
  provider: string,
  callbackURL: string,
  process: 'login' | 'connect' = 'login',
): Promise<string> {
  const result = await allAuthRequest('/auth/provider/redirect', {
    method: 'POST',
    body: JSON.stringify({
      provider,
      callback_url: callbackURL,
      process,
    }),
  });
  const url = result.data?.url;
  if (!url) {
    throw new Error('Provider did not return a redirect URL.');
  }
  return url;
}

// ─── Email verification ─────────────────────────────────────────────

/**
 * Verify an email with the key from the verification link. On success
 * the backend establishes a session cookie and may return a fresh
 * `meta.access_token` (Aegix's JWTTokenStrategy).
 *
 * The caller is responsible for plumbing the resulting JWT + user
 * into the auth-identity state machine — see `useVerifyEmail`'s
 * persist-and-reload bridge.
 */
export async function verifyEmail(
  key: string,
): Promise<AllAuthEndpointResponse> {
  return allAuthRequest('/auth/email/verify', {
    method: 'POST',
    body: JSON.stringify({ key }),
  });
}

// ─── Password recovery ──────────────────────────────────────────────

/** Request a password-reset email for the given address. */
export async function requestPasswordReset(email: string): Promise<void> {
  await allAuthRequest('/auth/password/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

/**
 * Reset password using the key from the reset email. Does not
 * auto-login on success — the user navigates to /login and signs in
 * with the new password.
 */
export async function resetPassword(
  key: string,
  password: string,
): Promise<AllAuthEndpointResponse> {
  return allAuthRequest('/auth/password/reset', {
    method: 'POST',
    body: JSON.stringify({ key, password }),
  });
}

// ─── Passkey REGISTRATION (login is in the lib adapter) ─────────────

export interface PasskeyCreationOptions {
  data: {
    creation_options: PublicKeyCredentialCreationOptions;
  };
}

/**
 * Request WebAuthn creation options for registering a new passkey to
 * the currently-authenticated user. The browser ceremony runs in
 * `PasskeyButton` / wherever it's invoked; the resulting credential
 * is sent via `registerPasskey()`.
 */
export async function getPasskeyCreationOptions(): Promise<PasskeyCreationOptions> {
  const response = await allAuthRequest('/auth/webauthn/signup', {
    method: 'GET',
  });
  // The /webauthn/signup GET response embeds `data.creation_options`;
  // the helper's `AllAuthEndpointResponse` is a permissive superset so
  // a narrow cast here is acceptable.
  return response as unknown as PasskeyCreationOptions;
}

/**
 * Register a new passkey credential for the currently-authenticated
 * user. Does not change auth state — the user is already logged in;
 * this only persists the new credential server-side.
 */
export async function registerPasskey(
  name: string,
  credential: PublicKeyCredential,
): Promise<void> {
  await allAuthRequest('/auth/webauthn/signup', {
    method: 'POST',
    body: JSON.stringify({
      name,
      credential: serializeCredential(credential),
    }),
  });
}

// ─── WebAuthn serialization helpers ─────────────────────────────────

/**
 * Serialize a `PublicKeyCredential` (creation OR assertion) into the
 * base64url-encoded JSON shape allauth's headless API expects.
 */
function serializeCredential(
  credential: PublicKeyCredential,
): Record<string, unknown> {
  const response = credential.response as
    | AuthenticatorAssertionResponse
    | AuthenticatorAttestationResponse;
  const result: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
    },
  };

  if ('authenticatorData' in response) {
    (result.response as Record<string, unknown>).authenticatorData =
      bufferToBase64url(response.authenticatorData);
    (result.response as Record<string, unknown>).signature =
      bufferToBase64url(response.signature);
    if (response.userHandle) {
      (result.response as Record<string, unknown>).userHandle =
        bufferToBase64url(response.userHandle);
    }
  }

  if ('attestationObject' in response) {
    (result.response as Record<string, unknown>).attestationObject =
      bufferToBase64url(response.attestationObject);
  }

  return result;
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}
